import type { Lead } from '../../types/lead.js';
import { LEAD_QUALITIES, SERVICE_FLAGS } from '../../types/lead.js';
import { LEAD_OUTCOME_TABLE } from '../../types/leadOutcome.js';
import { hidesTest } from './testVisibility.js';
import type { LeadFilters, LeadFunnel, LeadListRow, LeadSpeedBand } from '../../types/leadList.js';

// THE LEAD LIST AND FUNNEL RULES (Speed to Lead Stage 4.5C). Pure: one definition of every filter and every funnel figure,
// used by the server query and asserted by its tests. No clock reads.

/** An old response with no stored enquiry is trusted against the lead's first-ever receipt only when it came this soon after it. */
export const LEGACY_SPEED_WINDOW_SECONDS = 3600;

/**
 * Seconds from the ENQUIRY THAT EARNED THE RESPONSE arriving to the system's first response; null when there was none.
 * ⚠️ NOT from the lead record's first-ever `receivedAt`: a lead that enquired again a day later read as "Call 24:52:01". The enquiry is
 * stored beside the response (`firstResponseEnquiryAt`). A response recorded BEFORE that field existed falls back to the lead's
 * receivedAt only when it came within an hour of it (the first enquiry plainly answered), else there is no speed: a missing figure
 * beats a day-long one. A manual call is never recorded as a first response, so it never reaches here.
 */
export function speedToLeadSeconds(lead: Pick<Lead, 'receivedAt' | 'firstResponseAt'> & Partial<Pick<Lead, 'firstResponseEnquiryAt'>>): number | null {
  if (!lead.firstResponseAt) return null;
  const from = lead.firstResponseEnquiryAt ?? lead.receivedAt;
  const s = Math.round((Date.parse(lead.firstResponseAt) - Date.parse(from)) / 1000);
  if (!Number.isFinite(s) || s < 0) return null;
  if (!lead.firstResponseEnquiryAt && s > LEGACY_SPEED_WINDOW_SECONDS) return null;
  return s;
}

export function speedBand(seconds: number | null): LeadSpeedBand {
  if (seconds === null) return 'none';
  if (seconds < 300) return 'under5';
  if (seconds < 1800) return 'under30';
  return 'over30';
}

export function matchesFilters(lead: Lead, f: LeadFilters): boolean {
  if (f.q) {
    const q = f.q.trim().toLowerCase();
    if (q) {
      const digits = q.replace(/\D/g, '');
      const nameHit = (lead.name ?? '').toLowerCase().includes(q);
      const phoneHit = digits.length >= 3 && lead.phone.replace(/\D/g, '').endsWith(digits.slice(-4));
      if (!nameHit && !phoneHit) return false;
    }
  }
  if (f.receivedFrom && lead.receivedAt < f.receivedFrom) return false;
  if (f.receivedTo && lead.receivedAt > f.receivedTo) return false;
  if (f.quality && f.quality.length > 0 && !f.quality.includes(lead.profile.quality)) return false;
  if (f.contactStatus && lead.contact.status !== f.contactStatus) return false;
  if (f.outcome && lead.outcome.code !== f.outcome) return false;
  if (f.needsPerson !== undefined && lead.needsPerson.on !== f.needsPerson) return false;
  if (f.services && f.services.some((s) => lead.services[s] === null)) return false;
  if (f.activeSince && lead.lastActivityAt < f.activeSince) return false;
  if (f.speed && speedBand(speedToLeadSeconds(lead)) !== f.speed) return false;
  return true;
}

export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? s[mid]! : Math.round((s[mid - 1]! + s[mid]!) / 2);
}

/**
 * Leads -> Contacted (split by how the lead was first reached) -> Committed -> Joined. Test leads are not counted.
 * ⚠️ `Committed` is committed OR sale (a joined lead has plainly committed), and `Joined` is sale only, so Joined is
 * never greater than Committed, and neither is read as the other.
 */
export function computeFunnel(leads: Lead[], capped = false, testVisible?: ReadonlySet<string>): LeadFunnel {
  // ⚠️ A HISTORIC lead (no contact by us, ever) is in no funnel figure: it would only dilute the contacted and speed figures.
  const real = leads.filter((l) => !hidesTest(l, l.isTest, testVisible) && !l.historic);
  const speeds = real.map(speedToLeadSeconds).filter((s): s is number => s !== null);
  const stage = (l: Lead) => LEAD_OUTCOME_TABLE[l.outcome.code].stage;
  return {
    leads: real.length,
    contacted: real.filter((l) => l.contact.status !== 'not_contacted').length,
    contactedViaSam: real.filter((l) => l.contact.status !== 'not_contacted' && l.contact.via === 'sam').length,
    contactedViaCall: real.filter((l) => l.contact.status !== 'not_contacted' && l.contact.via === 'call').length,
    committed: real.filter((l) => stage(l) === 'committed' || stage(l) === 'sale').length,
    joined: real.filter((l) => stage(l) === 'sale').length,
    medianSpeedSeconds: median(speeds),
    respondedCount: speeds.length,
    capped,
  };
}

/** The list row, built field by field. ⚠️ Nothing is spread from the stored doc and only the last four phone digits go out. */
export function toLeadListRow(lead: Lead, accountName: string | null, assistantName: string | null = null): LeadListRow {
  const secs = speedToLeadSeconds(lead);
  const services = Object.fromEntries(SERVICE_FLAGS.map((f) => [f, lead.services[f] !== null])) as LeadListRow['services'];
  const call = lead.latestCallRef ? `${lead.latestCallRef.kind}:${lead.latestCallRef.id}` : null;
  return {
    id: lead.id,
    accountId: lead.accountId,
    accountName,
    assistantName,
    name: lead.name,
    phoneLast4: lead.phone.replace(/\D/g, '').slice(-4),
    source: lead.source,
    isTest: lead.isTest,
    receivedAt: lead.receivedAt,
    lastActivityAt: lead.lastActivityAt,
    quality: LEAD_QUALITIES.includes(lead.profile.quality) ? lead.profile.quality : 'unknown',
    goal: lead.profile.goal,
    motivation: lead.profile.motivation,
    preferredTimes: lead.profile.preferredTimes,
    services,
    contactStatus: lead.contact.status,
    contactVia: lead.contact.via,
    outcome: lead.outcome.code,
    outcomeSetBy: lead.outcome.setBy,
    needsPerson: lead.needsPerson.on,
    speedToLeadSeconds: secs,
    firstResponseVia: lead.firstResponseVia,
    historic: lead.historic === true,
    historyHandle: lead.latestConversationId || call ? { ...(call ? { call } : {}), ...(lead.latestConversationId ? { conversation: lead.latestConversationId } : {}) } : null,
  };
}

/** "3 min", "42 sec", "2 h 5 min": the speed to lead as the list and funnel show it. */
export function formatSpeed(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds} sec`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} min`;
}
